import { useMutation } from '@apollo/client';
import format from 'date-fns/format';

import { useCompleteFragment } from '@eluve/apollo-client';
import { EditableTitle } from '@eluve/blocks';
import { Stack, Text, useToast } from '@eluve/components';
import {
  useAppointmentContext,
  useIsUserAppointmentOwner,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';

import { useIsFeatureFlagEnabled } from './useIsFeatureFlagEnabled';

const appointmentNameAndDateFragment = graphql(`
  fragment AppointmentNameFragment on Appointments {
    name
    startDate
  }
`);

const updateAppointmentNameMutation = graphql(`
  mutation UpdateAppointmentName($tenantId: uuid!, $id: uuid!, $name: String!) {
    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: { name: $name }
    ) {
      id
      name
    }
  }
`);

interface AppointmentNameProps {
  readonly?: boolean;
}

export const AppointmentName: React.FC<AppointmentNameProps> = ({
  readonly,
}) => {
  const toast = useToast();
  const { tenantId, appointmentId } = useAppointmentContext();
  const isAppointmentTitleReadOnly = useIsFeatureFlagEnabled(
    'APPOINTMENT_TITLE_READONLY',
  );
  const isUserApptOwner = useIsUserAppointmentOwner();
  const isReadOnly =
    Boolean(readonly) || isAppointmentTitleReadOnly || !isUserApptOwner;

  const { name, startDate } = useCompleteFragment({
    fragment: appointmentNameAndDateFragment,
    key: {
      id: appointmentId,
    },
  });

  const [updateAppointmentName] = useMutation(updateAppointmentNameMutation, {
    optimisticResponse: (variables) => ({
      updateAppointmentsByPk: {
        id: variables.id,
        name: variables.name,
        __typename: 'Appointments',
      },
    }),
    onError: () => {
      toast.error('Failed to update appointment name');
    },
  });

  const handleNameUpdate = (name: string) => {
    updateAppointmentName({
      variables: {
        tenantId,
        id: appointmentId,
        name,
      },
    });
  };

  return (
    <Stack fullWidth={false} gap={1}>
      <Text color="tertiary">
        {startDate && format(new Date(startDate), 'MMMM d, yyyy')}
      </Text>
      <EditableTitle
        title={name}
        onTitleChange={handleNameUpdate}
        label="Update appointment name"
        isReadonly={isReadOnly}
      />
    </Stack>
  );
};
